import { useQuery, useMutation } from '@tanstack/react-query';
import http from '../../../utils/http';
import { Dir } from '../../../modules/dirs';
import {
  FeedbackArgs,
  MaintenanceRequestArgs,
  postSubmitFeedback,
  postSubmitIncident,
  postSubmitMaintenanceRequest,
} from './api';
import './translations';
import { useShallowEqualEffect } from '../../../utils/use-shallow-effect';

export function useDeviceTypes() {
  return useQuery({
    queryKey: ['app_device_types'],
    queryFn: () => http.get('/api/device-types') as unknown as any[],
  });
}

export function useCreateAssetSeriesSpec(id: Dir['id'] | undefined) {
  return useQuery({
    queryKey: ['app_device_create_assets_series_spec', id],
    queryFn: () =>
      http.get<
        never,
        {
          prefix: string;
          startFrom: number;
        }
      >(`/api/device/add-series/${id}`),
    enabled: !!id,
    gcTime: 1,
  });
}

export function useCreateNewDevice() {
  return useMutation<any, any, any>({
    mutationFn: (payload) => http.post('/api/device', payload),
  });
}

export function useCreateDeviceSeries() {
  return useMutation<any, any, any>({
    mutationFn: (payload) => {
      const typeId = payload['device_type'] || payload['type'];

      return http.post(`/api/device/add-series/${typeId}`, {
        ...payload,
        startFrom: Number(payload.startFrom),
      });
    },
  });
}

type InfoResponse = {
  cover?: string;
  fields: {
    [key: string]: string;
  };
  commonIssues?: string[];
  [key: string]: string | any;
};

export function useFeedbackItemInfo(id: Dir['id']) {
  return useQuery({
    queryKey: [`app_device_feedback_item_info_${id}`],
    queryFn: () =>
      http.get<unknown, InfoResponse>(`/api/feedback-channel/info/${id}`),
  });
}

export function useSubmitIncident(id: Dir['id']) {
  return useMutation({
    mutationFn: (data: FeedbackArgs) => {
      return postSubmitIncident(id, data);
    },
  });
}

export function useSubmitFeedback(id: Dir['id']) {
  return useMutation({
    mutationFn: (data: FeedbackArgs) => {
      return postSubmitFeedback(id, data);
    },
  });
}

export function useSubmitMaintenanceRequest(id: Dir['id']) {
  return useMutation({
    mutationFn: (data: MaintenanceRequestArgs) => {
      return postSubmitMaintenanceRequest(id, data);
    },
  });
}

export type DeviceNoticeArgs = {
  content: string;
  files?: File[];
};

export function useSubmitDeviceNotice(id: Dir['id']) {
  return useMutation({
    mutationFn: (data: DeviceNoticeArgs) => {
      const formData = new FormData();
      formData.append('content', data.content);

      if (data.files) {
        (data.files || []).forEach((file) => formData.append('files[]', file));
      }

      return http.post(`/api/assets/notices/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
  });
}

export function useUpdateDeviceNotice(id: number) {
  return useMutation({
    mutationFn: (data: DeviceNoticeArgs) => {
      const formData = new FormData();
      formData.append('content', data.content);

      if (data.files) {
        (data.files || []).forEach((file) => formData.append('files[]', file));
      }

      return http.post(`/api/assets/notices/update/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
  });
}

export function useDeleteDeviceNotice(id: number) {
  return useMutation({
    mutationFn: () => {
      return http.delete(`/api/assets/notices/${id}`);
    },
  });
}

export function useDeviceNoticesList(id: Dir['id']) {
  return useQuery({
    queryKey: ['app_device_notices_list', id],
    queryFn: () => http.get<any, any[]>(`/api/assets/notices/${id}`),
    enabled: !!id,
  });
}
